import { ErrorCode, VrioOttError } from '@dtvgo/error';
import { PlayerErrorSeverity } from '@dtvgo/player-common-utils';

export function greatestCommonDivisor(a: number, b: number) {
  let x = a;
  let y = b;

  while (y) {
    const t = y;
    y = x % y;
    x = t;
  }
  return x;
}

export function simplifyFrac(num: number, den: number) {
  const gcd = greatestCommonDivisor(num, den);
  return [num / gcd, den / gcd];
}

export function getAspectRatio(dimensions: { width: number; height: number }) {
  const { width, height } = dimensions;

  const [num, den] = simplifyFrac(width, height);
  return `${num}:${den}`;
}

export function aspectRatioToFrac(aspectRatio: string) {
  const [num, den] = aspectRatio
    .split(':')
    .map((value) => Number.parseInt(value, 10));
  return [num, den];
}

export function getProportion(aspectRatio: string) {
  const [num, den] = aspectRatioToFrac(aspectRatio);
  return num / den;
}

export function getHeightByWidth(width: number, ratio: string) {
  const [num, den] = aspectRatioToFrac(ratio);
  return width * (den / num);
}

export function resolveAfter(ms: number) {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

export function getHoursMinutesSeconds(seconds: number) {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds - hours * 3600) / 60);
  const remainingSeconds = Math.floor(seconds - hours * 3600 - minutes * 60);
  return [hours, minutes, remainingSeconds];
}

export function formatSecondsInText(seconds: number) {
  const [hours, minutes] = getHoursMinutesSeconds(seconds);
  const formattedHours = hours > 0 ? ` ${hours}h` : '';
  const formattedMinutes = minutes > 0 ? ` ${minutes}m` : '';
  return `${formattedHours}${formattedMinutes}`.trim();
}

export function formatSecondsInTime(seconds: number) {
  const [hours, minutes, remainingSeconds] = getHoursMinutesSeconds(seconds);

  return `${hours}:${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
}

export function isConnected() {
  return navigator.onLine;
}

export function isErrorPlayerException(error: VrioOttError): boolean {
  return error.code === ErrorCode.PLAYER_EXCEPTION;
}

export function isPlayerErrorSeverityNonCritical(error: VrioOttError): boolean {
  return (
    error.data?.playerErrorSeverity === PlayerErrorSeverity.NON_CRITICAL ||
    error.data?.playerErrorSeverity === PlayerErrorSeverity.HANDLED
  );
}

export function isPlayerErrorSeverityCritical(error: VrioOttError): boolean {
  return error.data?.playerErrorSeverity === PlayerErrorSeverity.CRITICAL;
}

export function isErrorRecoverable(error: VrioOttError) {
  // if the error doesn't have a code, it's recoverable
  if (!error.code) return true;

  // Return true for PlayerException
  return isErrorPlayerException(error);
}

export function areEqualArrays<T>(
  arr1: T[],
  arr2: T[],
  compareFn: (a: T, b: T) => number,
) {
  if (arr1.length !== arr2.length) return false;

  const sortedArr1 = [...arr1].sort(compareFn);
  const sortedArr2 = [...arr2].sort(compareFn);

  for (const [index, element] of sortedArr1.entries()) {
    if (compareFn(element, sortedArr2[index]) !== 0) return false;
  }

  return true;
}

export function compareStrings(a: string, b: string) {
  if (a === b) return 0;
  return a < b ? -1 : 1;
}
